// Bounty Higher-or-Lower — guess which character has the bigger bounty.
// Fixed-length round game (like the quizzes): answer all rounds, mistakes
// don't end it. Shows progress, a timer, your score out of the total, and
// a limited number of skips. Has a One Piece background scene + music and
// reveals the correct answer each round.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import characterProfiles from '../data/characterProfiles';
import { saveScore } from '../services/leaderboardService';
import { recordPlay } from '../services/playHistory';
import { trackEvent } from '../services/analyticsService';
import {
  createChallenge, loadChallenge, submitChallengeScore, getSavedName, saveName,
} from '../services/challengeService';
import { useUsername } from '../hooks/useUsername';
import UsernameModal from '../components/UsernameModal/UsernameModal';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { bountyBattleText } from '../components/QuizPageText/quizPageText.data';
import './BountyBattle.css';
import useGameFullscreen from '../hooks/useGameFullscreen';

// Characters with a real, known numeric bounty.
const FIGHTERS = characterProfiles
  .map((c) => ({ ...c, bountyNum: Number(String(c.bounty || '').replace(/[^0-9]/g, '')) }))
  .filter((c) => c.bountyNum > 0 && c.image);

const TOTAL_ROUNDS = 11;   // matchups per game (scored out of 11, like the Legend bar)
const START_SKIPS = 3;
const BEST_KEY = 'bountyBattleBestV2';   // fresh slot — old key held streak data, not /10 scores

// Audio (already in /public/audio, used by the main quiz too)
const MUSIC = '/audio/game-song-background.mp3';
const SFX_CORRECT = '/audio/correct-ans.mp3';
const SFX_WRONG = '/audio/wrong-bounty.mp3';   // trimmed clip (0:41–0:42 of the ringtone)
const SFX_OVER = '/audio/game-over-show.mp3';

const formatBerry = (n) => '฿' + n.toLocaleString('en-US');
const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// --- Challenges -----------------------------------------------------------
//
// A challenge is the same game, not a similar one: the matchups come from a
// seed, so the person who opens the link gets the eleven pairs their friend
// got, in the same order, and the two scores mean the same thing. The seed and
// the challenger's score ride in the URL — no account, no server, nothing to
// store. Three spare pairs are drawn as well, so using a skip does not push
// the two players onto different matchups.

const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const newSeed = () => Math.floor(Math.random() * 0xFFFFFFFF) >>> 0;

const pairsFromSeed = (seed) => {
  const rnd = mulberry32(seed);
  const out = [];
  for (let i = 0; i < TOTAL_ROUNDS + START_SKIPS; i++) {
    let a = Math.floor(rnd() * FIGHTERS.length);
    let b = Math.floor(rnd() * FIGHTERS.length);
    let guard = 0;
    while ((b === a || FIGHTERS[b].bountyNum === FIGHTERS[a].bountyNum) && guard++ < 50) {
      b = Math.floor(rnd() * FIGHTERS.length);
    }
    out.push([FIGHTERS[a], FIGHTERS[b]]);
  }
  return out;
};

// base64url, so the link survives being pasted anywhere.
const packChallenge = ({ seed, name, score }) => {
  const json = JSON.stringify({ s: seed, n: (name || '').slice(0, 18), p: score });
  return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const unpackChallenge = (raw) => {
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(b64)));
    const o = JSON.parse(json);
    if (typeof o.s !== 'number' || typeof o.p !== 'number') return null;
    return { seed: o.s >>> 0, name: (o.n || 'A friend').slice(0, 18), score: o.p };
  } catch (e) {
    return null;   // a mangled link just starts a normal game
  }
};

const readRoomCode = () => {
  if (typeof window === 'undefined') return '';
  try {
    return (new URLSearchParams(window.location.search).get('r') || '').toUpperCase().slice(0, 12);
  } catch (e) {
    return '';
  }
};

const readChallenge = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('c');
    return raw ? unpackChallenge(raw) : null;
  } catch (e) {
    return null;
  }
};

const BountyBattle = () => {
  const navigate = useNavigate();
  const [challenge] = useState(readChallenge);          // the old one-shot link: name + score in the URL
  const [roomCode, setRoomCode] = useState(readRoomCode);   // the room this game belongs to, if any
  const [room, setRoom] = useState(null);                   // { room, players } once loaded
  const [roomError, setRoomError] = useState('');
  const [playerName, setPlayerName] = useState(getSavedName);
  const [nameDraft, setNameDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const answersRef = useRef([]);          // what was picked, for the server to mark
  const [seed, setSeed] = useState(() => (challenge ? challenge.seed : newSeed()));
  const [pairs, setPairs] = useState(() => pairsFromSeed(seed));
  const [slot, setSlot] = useState(0);                   // which drawn pair is on screen
  const [nextSpare, setNextSpare] = useState(TOTAL_ROUNDS);  // skips eat the spares, in order
  const [shareState, setShareState] = useState('');      // '' | 'copied' | 'shared'
  const [roundNum, setRoundNum] = useState(1);    // 1..TOTAL_ROUNDS
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [seconds, setSeconds] = useState(0);
  const [skips, setSkips] = useState(START_SKIPS);
  const [phase, setPhase] = useState('intro');    // intro | guess | reveal | done
  useGameFullscreen(phase === 'guess' || phase === 'reveal');
  const [chosen, setChosen] = useState(null);      // index the player picked
  const [wasCorrect, setWasCorrect] = useState(false);
  const [soundOn, setSoundOn] = useState(true);

  // Leaderboard
  const { username, avatar, hasUsername, register } = useUsername();
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const savedRef = useRef(false);

  const pair = pairs[slot] || pairs[0];

  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 600);

  const music = useRef(null);
  const soundOnRef = useRef(true);   // mirror of soundOn for use in listeners

  // Phone gets the full arc-scene background; desktop matches the other games.
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Set up looping background music once. It does NOT autoplay — it only
  // starts when the player hits "Start Game", so the intro screen stays quiet.
  useEffect(() => {
    const m = new Audio(MUSIC);
    m.loop = true;
    m.volume = 0.3;
    music.current = m;
    return () => { m.pause(); };
  }, []);

  // Timer — counts up only while actually playing (not intro / not finished).
  useEffect(() => {
    if (phase !== 'guess' && phase !== 'reveal') return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Save the result to the leaderboard once the game finishes.
  useEffect(() => {
    if (phase !== 'done' || savedRef.current) return;
    savedRef.current = true;
    const entry = {
      score,
      correctAnswers: score,
      incorrectAnswers: TOTAL_ROUNDS - score,
      totalTime: seconds,
      gameType: 'bounty',
      date: new Date().toISOString(),
    };
    recordPlay(entry);
    if (hasUsername && username) {
      saveScore({ ...entry, username, avatar });
    } else {
      setPendingSave(entry);
      if (!challenge && !roomCode) setShowUsernameModal(true);
    }
  }, [phase, score, seconds, hasUsername, username, avatar, challenge, roomCode]);

  // Username modal handlers (collect a name for first-time players).
  const handleUsernameSubmit = async (newUsername, password, selectedAvatar, email) => {
    const result = await register(newUsername, password, selectedAvatar, email);
    if (result.success && pendingSave) {
      await saveScore({ ...pendingSave, username: newUsername, avatar: selectedAvatar });
      setShowUsernameModal(false);
      setPendingSave(null);
    } else if (!result.success) {
      throw new Error(result.error);
    }
  };

  const handleUsernameSkip = () => {
    setShowUsernameModal(false);
    setPendingSave(null);
  };

  // Play a one-shot sound effect (fresh element so quick repeats overlap).
  const playSfx = (url) => {
    if (!soundOnRef.current) return;
    const a = new Audio(url);
    a.volume = 0.55;
    a.play().catch(() => {});
  };

  // Ensure music is playing (no-op if already playing or muted).
  const ensureMusic = () => {
    if (soundOnRef.current && music.current) music.current.play().catch(() => {});
  };

  const toggleSound = () => {
    setSoundOn((on) => {
      const next = !on;
      soundOnRef.current = next;
      if (music.current) {
        if (next) music.current.play().catch(() => {});
        else music.current.pause();
      }
      return next;
    });
  };

  // Start the game from the intro screen — this click also unlocks the music.
  const startGame = () => {
    savedRef.current = false;
    setSeconds(0);
    setPhase('guess');
    ensureMusic();
  };

  const guess = (idx) => {
    if (phase !== 'guess') return;
    answersRef.current = [...answersRef.current, { q: slot, pick: idx }];
    ensureMusic();
    const other = idx === 0 ? 1 : 0;
    const correct = pair[idx].bountyNum > pair[other].bountyNum;
    setChosen(idx);
    setWasCorrect(correct);
    setPhase('reveal');
    playSfx(correct ? SFX_CORRECT : SFX_WRONG);

    const newScore = correct ? score + 1 : score;
    if (correct) setScore(newScore);

    setTimeout(() => {
      if (roundNum >= TOTAL_ROUNDS) {
        // last round — finish the game
        if (newScore > best) {
          setBest(newScore);
          localStorage.setItem(BEST_KEY, String(newScore));
        }
        setPhase('done');
        if (challenge) {
          trackEvent('challenge_finished', {
            game: 'bounty',
            score: newScore,
            challenger_score: challenge.score,
            result: newScore > challenge.score ? 'won' : newScore === challenge.score ? 'tied' : 'lost',
            event_category: 'Challenge',
          });
        }
        if (music.current) music.current.pause();
        playSfx(SFX_OVER);
      } else {
        setRoundNum((r) => r + 1);
        setSlot(roundNum);          // rounds 1..11 sit in slots 0..10
        setChosen(null);
        setPhase('guess');
      }
    }, 1400);
  };

  // Re-roll the current matchup without using up a round (limited per game).
  const skip = () => {
    if (phase !== 'guess' || skips <= 0) return;
    ensureMusic();
    setSkips((n) => n - 1);
    setSlot(nextSpare);
    setNextSpare((n) => n + 1);
  };

  // Put this result on the room's table. The server keeps the best one per
  // name, so playing again can only improve your line.
  const sendResult = useCallback(async (code, name, finalScore) => {
    if (!code || !name) return;
    setSubmitting(true);
    try {
      const data = await submitChallengeScore({
        code, name, score: finalScore, time: seconds, answers: answersRef.current,
      });
      setRoom(data);
      setSubmitted(true);
      saveName(name);
      setPlayerName(name);
      trackEvent('challenge_result', {
        game: 'bounty',
        code,
        score: finalScore,
        players: data.players.length,
        event_category: 'Challenge',
      });
    } catch (e) {
      setRoomError(e.taken
        ? 'That name is taken in this room. Pick another one.'
        : 'Could not save your score. Try again in a moment.');
      if (e.taken) { setPlayerName(''); setSubmitted(false); }
    } finally {
      setSubmitting(false);
    }
  }, [seconds]);

  // Finished a room game with a name already known: send it straight away.
  useEffect(() => {
    if (phase !== 'done' || !roomCode || submitted || submitting) return;
    const name = username || playerName;
    if (name) sendResult(roomCode, name, score);
  }, [phase, roomCode, submitted, submitting, username, playerName, score, sendResult]);

  // Hand the friend the same eleven matchups and the score to beat.
  const roomUrl = (code) => `https://www.onepiecetrivia.com/bounty-battle?r=${code}`;

  // Straight to the clipboard, for people who would rather paste it themselves.
  const copyLink = async () => {
    if (!roomCode) return;
    try {
      await navigator.clipboard.writeText(roomUrl(roomCode));
      setShareState('copied');
      setTimeout(() => setShareState(''), 4000);
    } catch (e) {
      setRoomError('Could not copy. Hold the link to copy it by hand.');
    }
  };

  const challengeFriend = async () => {
    const name = username || playerName || 'A pirate';
    let code = roomCode;
    try {
      if (!code) {
        code = await createChallenge({ game: 'bounty', seed, rounds: TOTAL_ROUNDS, name });
        setRoomCode(code);
        await sendResult(code, name, score);
        trackEvent('challenge_created', { game: 'bounty', code, score, event_category: 'Challenge' });
      }
    } catch (e) {
      setRoomError('Could not make the link. Try again in a moment.');
      return;
    }

    const url = roomUrl(code);
    const text = `I scored ${score}/${TOTAL_ROUNDS} on One Piece Higher or Lower. Same eleven bounties — beat me.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'One Piece Higher or Lower', text, url });
        setShareState('shared');
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        setShareState('copied');
      }
    } catch (e) {
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        setShareState('copied');
      } catch (err) { /* nothing left to try */ }
    }
    setTimeout(() => setShareState(''), 4000);
  };

  const restart = useCallback(() => {
    savedRef.current = false;
    setSubmitted(false);
    answersRef.current = [];
    // Inside a room everyone plays the same matchups, so a rematch is the same
    // eleven again; on your own it is a brand new draw.
    const fresh = roomCode ? seed : newSeed();
    setSeed(fresh);
    setPairs(pairsFromSeed(fresh));
    setSlot(0);
    setNextSpare(TOTAL_ROUNDS);
    setRoundNum(1);
    setScore(0);
    setSeconds(0);
    setSkips(START_SKIPS);
    setChosen(null);
    setPhase('guess');
    if (music.current && soundOnRef.current) {
      music.current.currentTime = 0;
      music.current.play().catch(() => {});
    }
  }, [roomCode, seed]);

  useEffect(() => { document.title = 'Bounty Battle | One Piece Trivia'; }, []);

  // The room decides the matchups, so it is fetched before the game starts.
  useEffect(() => {
    let alive = true;
    if (!roomCode) return undefined;
    (async () => {
      const data = await loadChallenge(roomCode);
      if (!alive) return;
      if (!data) { setRoomError('That challenge link is not valid any more.'); return; }
      setRoom(data);
      setSeed(data.room.seed);
      // The matchups come from the match itself when the server has them, so
      // a later change to the cast cannot hand two players different games.
      const stored = data.room.questions;
      if (Array.isArray(stored) && stored.length) {
        const bySlug = {};
        FIGHTERS.forEach((f) => { bySlug[f.slug] = f; });
        const built = stored
          .map(([a, b]) => [bySlug[a], bySlug[b]])
          .filter(([a, b]) => a && b);
        if (built.length) setPairs(built);
      } else {
        setPairs(pairsFromSeed(data.room.seed));
      }
      setSlot(0);
      trackEvent('challenge_opened', {
        game: 'bounty',
        code: roomCode,
        players: data.players.length,
        event_category: 'Challenge',
      });
    })();
    return () => { alive = false; };
  }, [roomCode]);

  useEffect(() => {
    if (challenge) {
      trackEvent('challenge_opened', {
        game: 'bounty',
        challenger_score: challenge.score,
        event_category: 'Challenge',
      });
    }
  }, [challenge]);

  const card = (c, idx) => {
    const revealed = phase === 'reveal';
    const other = idx === 0 ? 1 : 0;
    const isHigher = revealed && pair[idx].bountyNum > pair[other].bountyNum;
    const isWrongPick = revealed && chosen === idx && !isHigher;
    const cls = isHigher ? 'correct' : isWrongPick ? 'wrong' : '';
    return (
      <button
        key={idx}
        className={`bb-card ${cls}`}
        onClick={() => guess(idx)}
        disabled={phase !== 'guess'}
      >
        <img src={c.image} alt={c.name} className="bb-img" />
        {isHigher && <span className="bb-badge">✓ Higher</span>}
        {isWrongPick && <span className="bb-badge bb-badge-wrong">✗ Lower</span>}
        <div className="bb-info">
          <span className="bb-name">{c.name}</span>
          {revealed ? (
            <span className="bb-bounty">{formatBerry(c.bountyNum)}</span>
          ) : (
            <span className="bb-hint">higher or lower?</span>
          )}
        </div>
      </button>
    );
  };

  const myName = (username || playerName || '').toLowerCase();

  const standings = (title) => room && (
    <div className="bb-room">
      <div className="bb-room-head">
        <span className="bb-room-title">{title}</span>
        <span className="bb-room-code">{room.room.code}</span>
      </div>

      {room.players.length === 0 ? (
        <p className="bb-room-empty">Nobody has played it yet. Be the first.</p>
      ) : (
        <ol className="bb-room-list">
          {room.players.map((p, i) => (
            <li
              key={p.name + i}
              className={`bb-room-row ${p.name.toLowerCase() === myName ? 'is-me' : ''}`}
            >
              <span className="bb-room-rank">{i + 1}</span>
              <span className="bb-room-name">{p.name}</span>
              <span className="bb-room-score">{p.score}/{room.room.rounds}</span>
              <span className="bb-room-time">{formatTime(p.totalTime)}</span>
              <span className="bb-room-tries">{p.attempts === 1 ? '1 try' : `${p.attempts} tries`}</span>
            </li>
          ))}
        </ol>
      )}

      <p className="bb-room-foot">
        {room.players.length === 1 ? '1 player' : `${room.players.length} players`}
        {' · '}
        {(() => {
          const games = room.players.reduce((n, p) => n + p.attempts, 0);
          return games === 1 ? '1 game played' : `${games} games played`;
        })()}
        {' · '}
        opened {room.room.opens} {room.room.opens === 1 ? 'time' : 'times'}
      </p>
    </div>
  );

  const missed = TOTAL_ROUNDS - score;

  return (
    <>
      <Helmet>
        <title>One Piece Higher or Lower — Guess the Bigger Bounty</title>
        <meta name="description" content="Two pirates, two bounties — guess which is higher. Eleven rounds against the clock, real bounties from the manga, and a leaderboard. Free, no signup." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/bounty-battle" />
        <meta property="og:title" content="One Piece Higher or Lower — Guess the Bigger Bounty" />
        <meta property="og:description" content="The One Piece higher or lower game: see a bounty, then guess if the next pirate’s is higher or lower. Eleven rounds and a leaderboard." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/bounty-battle" />
      </Helmet>

      <div
        className="bb-container"
        style={isPhone ? {
          backgroundImage: `linear-gradient(rgba(15,22,38,0.84), rgba(15,22,38,0.94)), url(${process.env.PUBLIC_URL}/arcs/marineford.webp)`,
        } : undefined}
      >
        <button className="bb-sound" onClick={toggleSound} aria-label="Toggle sound" title="Toggle sound">
          {soundOn ? '🔊' : '🔇'}
        </button>

        <h1 className="bb-title">Bounty Battle: Higher or Lower</h1>
        <p className="bb-sub">Is the next bounty <strong>higher or lower?</strong> Tap to guess.</p>

        {phase !== 'intro' && (
          <div className="bb-scores">
            <span>⏱ Time: <strong>{formatTime(seconds)}</strong></span>
            {phase !== 'done' && <span>Round: <strong>{roundNum}/{TOTAL_ROUNDS}</strong></span>}
            <span>Score: <strong>{score}</strong></span>
            {best > 0 && <span>Best: <strong>{best}/{TOTAL_ROUNDS}</strong></span>}
          </div>
        )}

        {phase === 'intro' ? (
          <div className="bb-intro">
            {room ? (
              <>
                {standings('The challenge so far')}
                <p className="bb-challenge-note">
                  Same {TOTAL_ROUNDS} bounties as everyone else here, in the same order.
                </p>
              </>
            ) : challenge ? (
              <div className="bb-challenge">
                <p className="bb-challenge-line">
                  <strong>{challenge.name}</strong> challenged you.
                </p>
                <p className="bb-challenge-score">
                  Their score: <strong>{challenge.score}/{TOTAL_ROUNDS}</strong>
                </p>
                <p className="bb-challenge-note">
                  You get the same {TOTAL_ROUNDS} bounties, in the same order. Beat it.
                </p>
              </div>
            ) : (
              <p className="bb-intro-text">
                Guess who has the <strong>higher bounty</strong> — {TOTAL_ROUNDS} rounds, {START_SKIPS} skips.
                {best > 0 && <> Your best: <strong className="bb-intro-best">{best}/{TOTAL_ROUNDS}</strong>.</>}
              </p>
            )}

            <div className="bb-arena bb-arena-preview">
              {card(pair[0], 0)}
              <div className="bb-vs">VS</div>
              {card(pair[1], 1)}
            </div>

            <button className="bb-btn bb-btn-primary bb-start-btn" onClick={startGame}>▶ Start Game</button>
          </div>
        ) : phase !== 'done' ? (
          <>
            <div className="bb-progress">
              <div className="bb-progress-fill" style={{ width: `${((roundNum - 1) / TOTAL_ROUNDS) * 100}%` }} />
            </div>

            <div className="bb-arena">
              {card(pair[0], 0)}
              <div className="bb-vs">VS</div>
              {card(pair[1], 1)}
            </div>

            <button
              className="bb-skip"
              onClick={skip}
              disabled={phase !== 'guess' || skips <= 0}
            >
              ⏭ Skip {skips > 0 ? `(${skips} left)` : '(none left)'}
            </button>
          </>
        ) : (
          <div className="bb-gameover">
            <h2>Finished!</h2>
            <p className="bb-final">
              You scored <strong>{score}/{TOTAL_ROUNDS}</strong>
            </p>
            <p className="bb-meta">{missed} missed · time {formatTime(seconds)}</p>
            {score >= best && score > 0 && <p className="bb-record">🏴‍☠️ New best!</p>}

            {challenge && (
              <div className={`bb-verdict ${score > challenge.score ? 'won' : score === challenge.score ? 'tied' : 'lost'}`}>
                <p className="bb-verdict-line">
                  You <strong>{score}</strong> — {challenge.name} <strong>{challenge.score}</strong>
                </p>
                <p className="bb-verdict-word">
                  {score > challenge.score
                    ? 'You win. Send it back to them.'
                    : score === challenge.score
                    ? 'Dead even. Play it again.'
                    : `${challenge.name} still holds it.`}
                </p>
              </div>
            )}

            {roomCode && !submitted && !username && !playerName && (
              <div className="bb-name-ask">
                <label htmlFor="bb-name">Your name for the table</label>
                <div className="bb-name-row">
                  <input
                    id="bb-name"
                    className="bb-name-input"
                    maxLength={18}
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    placeholder="e.g. Fahad"
                  />
                  <button
                    className="bb-btn bb-btn-primary"
                    disabled={!nameDraft.trim() || submitting}
                    onClick={() => sendResult(roomCode, nameDraft.trim(), score)}
                  >
                    {submitting ? 'Saving…' : 'Add me'}
                  </button>
                </div>
              </div>
            )}

            {roomCode && submitted && standings('Standings')}

            {roomCode && (
              <div className="bb-invite">
                <p className="bb-invite-label">Send this to your friends</p>
                <div className="bb-invite-row">
                  <input
                    className="bb-invite-link"
                    readOnly
                    value={`onepiecetrivia.com/bounty-battle?r=${roomCode}`}
                    onFocus={(e) => e.target.select()}
                    aria-label="Challenge link"
                  />
                  <button className="bb-btn bb-btn-copy" onClick={copyLink}>
                    {shareState === 'copied' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <p className="bb-invite-code">
                  Or give them the code: <strong>{roomCode}</strong>
                </p>
              </div>
            )}

            {roomError && <p className="bb-room-error">{roomError}</p>}

            {(challenge || roomCode) && pendingSave && (
              <button className="bb-btn bb-btn-primary" onClick={() => setShowUsernameModal(true)}>
                🏆 Save to the global leaderboard
              </button>
            )}

            <button className="bb-btn bb-btn-challenge" onClick={challengeFriend}>
              {shareState === 'copied' ? '✓ Link copied'
                : shareState === 'shared' ? '✓ Sent'
                : roomCode ? '⚔️ Invite another friend'
                : '⚔️ Challenge a friend'}
            </button>
            <button className="bb-btn bb-btn-primary" onClick={restart}>Play again</button>
            <button className="bb-btn" onClick={() => navigate('/leaderboard')}>View leaderboard</button>
          </div>
        )}
      </div>

      <UsernameModal
        isOpen={showUsernameModal}
        standing={pendingSave && { gameType: 'bounty', score: pendingSave.score, totalTime: pendingSave.totalTime, max: 11 }}
        onSubmit={handleUsernameSubmit}
        onSkip={handleUsernameSkip}
        initialUsername=""
      />
      <QuizPageText {...bountyBattleText} />
    </>
  );
};

export default BountyBattle;
